import { Inject, Injectable } from '@angular/core';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { FileStorageInjectionToken, FileStorageServiceInterface } from '../../interfaces/file-storage.interface';
import { RecipientsInterface } from '../../interfaces/recipients.interface';
import { NotificationIdentifierModel } from '../../models/low-level/notification-identifier.model';
import { JsonRecipientModel, RecipientModel, RecipientModelFromJson } from '../../models/low-level/recipient.model';
import { LocalStorageCommonService } from './local-storage-common.service';
import { isErrorWithCode } from '../../utilities/is-error-with-code';

const KEY_RECIPIENTS = 'RECIPIENTS';

@Injectable({
    providedIn: 'root',
})
export class LocalStorageRecipientsService implements RecipientsInterface {
    private recipientsStorage: SavedRecipientsInterface[] = [];
    public constructor(
        @Inject(FileStorageInjectionToken) private readonly fileStorage: FileStorageServiceInterface,
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        private readonly localStorageCommonService: LocalStorageCommonService,
    ) {}

    public init(): Observable<void> {
        return this.fileStorage.loadCompressedData<JsonRecipientsInterface[]>(KEY_RECIPIENTS).pipe(
            map((savedRecipients: JsonRecipientsInterface[]) => {
                savedRecipients.map((jsonRecipient) => {
                    const recipientModel = jsonRecipient.recipientsModel.map((recipient) => {
                        return new RecipientModelFromJson(recipient);
                    });

                    const recipients: SavedRecipientsInterface = {
                        id: jsonRecipient.id,
                        recipientsModel: recipientModel,
                    };
                    this.recipientsStorage.push(recipients);
                });
            }),
            catchError((error) => {
                if (isErrorWithCode(error) && error.code === 1) {
                    this.log.debug(
                        `MaterialUtilitiesService: getRecipientsFromLocalStorage: File was not found, returning default value instead`,
                    );
                }
                return of(undefined);
            }),
        );
    }

    public getRecipientsByNotificationId(id: NotificationIdentifierModel): RecipientModel[] | undefined {
        const storageId = this.generateId(id);

        return this.recipientsStorage.find((recipients) => recipients.id === storageId)?.recipientsModel;
    }

    public saveRecipientsByNotificationId(
        recipients: RecipientModel[],
        id: NotificationIdentifierModel,
    ): Observable<SavedRecipientsInterface[]> {
        const storageId = this.generateId(id);
        const recipientsStorage: SavedRecipientsInterface = {
            id: storageId,
            recipientsModel: recipients,
        };

        const i = this.recipientsStorage.findIndex((recipient) => recipient.id === storageId);
        if (i !== -1) {
            this.recipientsStorage[i] = recipientsStorage;
        } else {
            this.recipientsStorage.push(recipientsStorage);
        }

        this.log.debug(`${this.constructor.name}: saveRecipientsByNotificationId`, {
            storageId,
        });
        return this.saveRecipientsToLocalStorage();
    }

    private saveRecipientsToLocalStorage(): Observable<SavedRecipientsInterface[]> {
        return this.fileStorage.storeCompressedData(KEY_RECIPIENTS, this.recipientsStorage);
    }

    public removeRecipientsFromLocalStorage(id: NotificationIdentifierModel): Observable<SavedRecipientsInterface[]> {
        const storageId = this.generateId(id);
        const index = this.recipientsStorage.findIndex((recipient) => recipient.id === storageId);

        if (index !== -1) {
            this.recipientsStorage.splice(index, 1);
        }

        this.log.debug(`${this.constructor.name}: removeRecipientsFromLocalStorage`, {
            storageId,
            index,
        });
        return this.saveRecipientsToLocalStorage();
    }

    public generateId(id: NotificationIdentifierModel): string {
        return KEY_RECIPIENTS + '_' + this.localStorageCommonService.makeId(id);
    }
}

export interface SavedRecipientsInterface {
    id: string;
    recipientsModel: RecipientModel[];
}

export interface JsonRecipientsInterface {
    id: string;
    recipientsModel: JsonRecipientModel[];
}
